import { useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { http, formatApiError } from "../lib/api";
import { toast } from "../lib/notify";
import { Trash2, RotateCcw, X, AlertTriangle } from "lucide-react";

const TRASH_BY_PATH = [
  ["/tanimlar/projeler", ["projects", "regions", "locations", "assignments", "categories", "services"]],
  ["/tanimlar/hizmetler", ["categories", "services"]],
  ["/cariler", ["caris"]],
  ["/faturalar", ["invoices"]],
  ["/cekler", ["checks"]],
  ["/veri-girisi", ["entries", "invoices", "checks", "bank_loans", "personnel"]],
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

function PurgeConfirm({ count, onConfirm, onCancel, busy }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="trash-purge-confirm">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-bold text-slate-800">Kalıcı olarak silinsin mi?</div>
            <div className="text-xs text-slate-600 mt-1">{count} kayıt çöp kutusundan tamamen silinecek. Bu işlem geri alınamaz.</div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onCancel} disabled={busy} data-testid="trash-purge-cancel" className="px-3 py-1.5 rounded-md border border-slate-300 text-xs font-semibold text-slate-700 hover:bg-slate-50">Vazgeç</button>
          <button onClick={onConfirm} disabled={busy} data-testid="trash-purge-ok" className="px-3 py-1.5 rounded-md bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-50">Kalıcı Sil</button>
        </div>
      </div>
    </div>
  );
}

export function TrashPanel({ modules, buttonClassName, onRestored }) {
  const location = useLocation();
  const effectiveModules = modules ?? TRASH_BY_PATH.find(([p]) => location.pathname.startsWith(p))?.[1];
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [purgeIds, setPurgeIds] = useState(null);

  const allSelected = !!items?.length && selected.length === items.length;
  const toggleAll = () => setSelected(allSelected ? [] : items.map((x) => x.id));
  const toggleOne = (id) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  const load = async () => {
    const params = effectiveModules?.length ? { modules: effectiveModules.join(",") } : {};
    const { data } = await http.get("/trash", { params });
    setItems(data);
    setSelected([]);
  };

  const openPanel = async () => {
    setOpen(true);
    setItems(null);
    try { await load(); } catch (e) { toast.error(formatApiError(e)); setItems([]); }
  };

  // tek istek: yüzlerce kayıt da olsa parça parça değil, toplu işlenir
  const run = async (ids, action, doneMsg) => {
    if (!ids.length) return;
    setBusy(true);
    try {
      const picked = items.filter((x) => ids.includes(x.id)).map((x) => ({ module: x.module, id: x.id }));
      const { data } = await http.post("/trash/bulk", { action, items: picked });
      if (data.missing > 0) toast.info(`${data.count} kayıt ${doneMsg}; ${data.missing} kayıt bağlı olduğu kayıtla birlikte zaten işlenmişti`);
      else toast.success(`${data.count} kayıt ${doneMsg}`);
      await load();
      window.dispatchEvent(new Event("focus")); // sayfalar odaklanınca listelerini tazeliyor
      onRestored?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      setPurgeIds(null);
    }
  };

  const restore = (ids) => run(ids, "restore", "geri alındı");
  const purge = (ids) => run(ids, "purge", "kalıcı olarak silindi");

  return (
    <>
      <button onClick={openPanel} data-testid="trash-open" className={buttonClassName} title="Silinen kayıtları görüntüle ve geri al">
        <Trash2 className="w-4 h-4" /> Silinenler
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="trash-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200">
              <Trash2 className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-bold text-slate-800">Silinen Kayıtlar</span>
              <span className="text-[11px] text-slate-400">Seçip geri alabilir veya kalıcı olarak silebilirsiniz</span>
              <button onClick={() => setOpen(false)} className="ml-auto text-slate-400 hover:text-slate-700" data-testid="trash-close"><X className="w-4 h-4" /></button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {items === null ? (
                <div className="py-10 text-center text-xs text-slate-400">Yükleniyor…</div>
              ) : items.length === 0 ? (
                <div className="py-10 text-center text-xs text-slate-400" data-testid="trash-empty">Silinmiş kayıt yok.</div>
              ) : (
                <table className="w-full tbl">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left w-8">
                        <input type="checkbox" data-testid="trash-select-all" checked={allSelected} onChange={toggleAll} title="Tümünü seç / kaldır" />
                      </th>
                      {["Menü", "Kayıt", "Silen · Tarih", "Gerekçe", ""].map((h, i) => (
                        <th key={h || `col-${i}`} className={`px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold ${i === 4 ? "text-right" : "text-left"}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.id} className={`border-t border-slate-100 ${selected.includes(it.id) ? "bg-amber-50/60" : ""}`} data-testid={`trash-item-${it.id}`}>
                        <td className="px-3 py-2">
                          <input type="checkbox" data-testid={`trash-select-${it.id}`} checked={selected.includes(it.id)} onChange={() => toggleOne(it.id)} />
                        </td>
                        <td className="px-3 py-2 text-xs"><span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-semibold">{it.module_label}</span></td>
                        <td className="px-3 py-2 text-xs font-medium">{it.label}</td>
                        <td className="px-3 py-2 text-xs text-slate-500" data-testid={`trash-meta-${it.id}`}>
                          {(it.deleted_by_name || it.deleted_by) && <div className="font-semibold text-slate-700">{it.deleted_by_name || it.deleted_by}</div>}
                          <div>{fmtDate(it.deleted_at)}</div>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600 max-w-[220px]" data-testid={`trash-reason-${it.id}`}>{it.deleted_reason || <span className="text-slate-300">—</span>}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <button onClick={() => restore([it.id])} disabled={busy} data-testid={`trash-restore-${it.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-emerald-700 text-emerald-800 text-[11px] font-semibold hover:bg-emerald-50 disabled:opacity-50">
                            <RotateCcw className="w-3 h-3" /> Geri Al
                          </button>
                          <button onClick={() => setPurgeIds([it.id])} disabled={busy} data-testid={`trash-purge-${it.id}`} title="Kalıcı olarak sil"
                            className="ml-1.5 inline-flex items-center gap-1 px-2 py-1 rounded-md border border-red-300 text-red-700 text-[11px] font-semibold hover:bg-red-50 disabled:opacity-50">
                            <Trash2 className="w-3 h-3" /> Sil
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {selected.length > 0 && (
              <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-t border-slate-200 bg-amber-50" data-testid="trash-bulk-bar">
                <span className="text-xs font-semibold text-amber-900" data-testid="trash-selected-count">{selected.length} kayıt seçildi</span>
                <button onClick={() => restore(selected)} disabled={busy} data-testid="trash-restore-selected"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-800 disabled:opacity-50">
                  <RotateCcw className="w-3.5 h-3.5" /> Seçilenleri Geri Al
                </button>
                <button onClick={() => setPurgeIds(selected)} disabled={busy} data-testid="trash-purge-selected"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-50">
                  <Trash2 className="w-3.5 h-3.5" /> Seçilenleri Kalıcı Sil
                </button>
                <button onClick={() => setSelected([])} className="text-xs text-slate-500 hover:text-slate-800" data-testid="trash-clear-selection">Vazgeç</button>
              </div>
            )}
          </div>

          {purgeIds && <PurgeConfirm count={purgeIds.length} busy={busy} onCancel={() => setPurgeIds(null)} onConfirm={() => purge(purgeIds)} />}
        </div>,
        document.body,
      )}
    </>
  );
}
